import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpTrayIcon, PhotoIcon, VideoCameraIcon, DocumentIcon, XMarkIcon } from '@heroicons/react/24/outline';

const ACCEPT = 'image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,.7z';
const MAX_FILES = 10;

const formatSize = (bytes) => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(1)} KB`;
};

const fileKind = (file) => {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  return 'file';
};

// Small preview tile for a local File (object URL is revoked when the tile unmounts)
const FileThumb = ({ file, onRemove, size = 'h-20 w-28' }) => {
  const kind = fileKind(file);
  const url = useMemo(() => (kind === 'file' ? null : URL.createObjectURL(file)), [file, kind]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  return (
    <div className="relative group">
      <div className={`${size} rounded-md overflow-hidden border border-gray-200 bg-gray-100 flex items-center justify-center`}>
        {kind === 'image' && <img src={url} alt={file.name} className="h-full w-full object-cover" />}
        {kind === 'video' && <video src={`${url}#t=0.5`} muted preload="metadata" className="h-full w-full object-cover bg-black" />}
        {kind === 'file' && <DocumentIcon className="h-8 w-8 text-gray-400" />}
      </div>
      <p className="mt-1 w-28 truncate text-[11px] text-gray-700" title={file.name}>{file.name}</p>
      <p className="text-[10px] text-gray-400">{formatSize(file.size)}</p>
      <button
        type="button"
        onClick={onRemove}
        className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-white border border-gray-300 text-gray-500 hover:text-red-600 hover:border-red-300 flex items-center justify-center shadow-sm"
        aria-label={`Remove ${file.name}`}
      >
        <XMarkIcon className="h-3 w-3" />
      </button>
    </div>
  );
};

// Upload button + media picker popup. Selected files are kept by the parent and sent with the form.
const AttachmentUploader = ({ files, onChange }) => {
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [staged, setStaged] = useState([]);
  const [dragOver, setDragOver] = useState(false);

  const stage = (list) => {
    const incoming = Array.from(list || []);
    setStaged(prev => [...prev, ...incoming].slice(0, Math.max(0, MAX_FILES - files.length)));
  };

  const close = () => { setOpen(false); setStaged([]); setDragOver(false); };

  const confirm = () => {
    onChange([...files, ...staged]);
    close();
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={files.length >= MAX_FILES}
        className="px-4 h-10 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium text-sm flex items-center gap-2 disabled:opacity-50"
      >
        <ArrowUpTrayIcon className="h-4 w-4" /> Upload
      </button>
      <p className="mt-1 text-xs text-gray-500">Images, videos or documents · up to {MAX_FILES} files</p>

      {files.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-4">
          {files.map((file, idx) => (
            <FileThumb key={`${file.name}-${file.size}-${idx}`} file={file} onRemove={() => onChange(files.filter((_, i) => i !== idx))} />
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[75] flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="fixed inset-0 bg-gray-900/50" onClick={close} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-base font-bold text-gray-900">Upload attachments</h3>
              <button type="button" onClick={close} className="text-gray-500 hover:text-gray-700" aria-label="Close">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); stage(e.dataTransfer.files); }}
                onClick={() => inputRef.current?.click()}
                className={`cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors ${dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'}`}
              >
                <div className="flex justify-center gap-2 text-gray-400 mb-2">
                  <PhotoIcon className="h-8 w-8" />
                  <VideoCameraIcon className="h-8 w-8" />
                </div>
                <p className="text-sm font-medium text-gray-700">Drag and drop files here, or <span className="text-blue-600">browse</span></p>
                <p className="text-xs text-gray-500 mt-1">Screenshots, screen recordings, PDFs and documents</p>
                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  accept={ACCEPT}
                  className="hidden"
                  onChange={(e) => { stage(e.target.files); e.target.value = ''; }}
                />
              </div>

              {staged.length > 0 && (
                <div className="flex flex-wrap gap-4 max-h-64 overflow-y-auto pt-2">
                  {staged.map((file, idx) => (
                    <FileThumb key={`${file.name}-${file.size}-${idx}`} file={file} onRemove={() => setStaged(prev => prev.filter((_, i) => i !== idx))} />
                  ))}
                </div>
              )}
            </div>

            <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2">
              <button type="button" onClick={close} className="px-4 h-10 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                type="button"
                onClick={confirm}
                disabled={staged.length === 0}
                className="px-4 h-10 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium text-sm disabled:opacity-50"
              >
                Add {staged.length > 0 ? `${staged.length} file${staged.length > 1 ? 's' : ''}` : 'files'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttachmentUploader;
